import type { ToolDefinition, ToolType } from "../types";

const SECURE_PASSWORD_GENERATOR_TOOL_TYPE = {
  label: "Security Tool",
  icon: "ShieldCheck",
} as const satisfies ToolType;

const securePasswordGeneratorTool: ToolDefinition = {
  slug: "secure-password-generator",
  name: "Secure Password Generator | Create Strong Random Passwords Online",
  title: "Secure Password Generator",
  description:
    "Use a secure password generator to create random passwords from 4 to 512 characters with symbols, numbers, exclusions, and forced included characters.",
  longDescription:
    "Protect your digital life with a complex password generator built for real account rules. Choose a length from 4 to 512 characters, toggle uppercase, lowercase, numbers, and symbols, exclude confusing characters, or force specific letters and symbols into the final result.",
  toolType: SECURE_PASSWORD_GENERATOR_TOOL_TYPE,
  heroBadges: [
    "100% Free",
    "No Logging",
    "No Account Required",
    "Works on Mobile",
    "Secure Processing",
  ],
  category: "Security Tools",
  categorySlug: "security-tools",
  href: "/tools/secure-password-generator",
  api: "/api/v1/secure-password-generator",
  keywords: [
    "snappy random password generator",
    "create strong passwords online",
    "how to make a secure login",
    "16 character alphanumeric phrase builder",
    "generate 128 bit encryption keys",
    "customizable pdf password maker",
    "easy to remember password ideas",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Generate a Secure Password",
        description:
          "Choose your password length, character types, exclusions, and required characters, then generate a secure password you can copy into your password manager.",
      },
      benefits: {
        title: "Why Use a Random Password Generator?",
        description:
          "A random password generator helps you avoid repeated, predictable, or human-made patterns when creating secure unique passwords for every account.",
      },
      faqs: {
        title: "Password Security FAQ",
        description:
          "Common questions about password length, strength, and best practices for 2026.",
      },
    },
    intro: {
      title: "Why Weak Passwords Still Cause Most Account Breaches",
      paragraphs: [
        "In an era of sophisticated cyberattacks, the biggest vulnerability for most users is still a predictable password. People reuse names, dates, keyboard patterns, and familiar words because they are easier to remember, but those patterns are exactly what dictionary attacks and credential stuffing tools test first. A safe password creator removes that habit by producing a high-entropy random password that is harder to guess and safer to store in a password manager.",
        "Our secure password generator lets you build unique password strings from 4 to 512 characters, whether you need a quick 16 character password generator for a work login, a random email password generator, a wifi password key generator, or a long random encryption key generator. You can toggle uppercase, lowercase, numbers, and symbols, then use include and exclude controls to satisfy awkward account rules without weakening the result.",
      ],
      note: "For maximum security, always save your generated results in a trusted password manager rather than writing them down.",
    },
    introBlocks: [
      {
        kind: "text",
        title: "Customizable Security for Every Platform",
        level: "h2",
        paragraphs: [
          "Not all websites have the same requirements. Some demand a strong password with special characters, some reject symbols, and some require at least one exact character. This customizable password generator fits those rules by letting you select uppercase letters, lowercase letters, numbers, symbols, excluded characters, and forced included characters before generation.",
          "The include field is useful when a form says your password must contain a particular symbol or letter. The exclude field solves the opposite problem: you can remove ambiguous characters like '0' and 'O', or '1' and 'l', when you need a human readable password that is easier to type from one device to another.",
        ],
        note: "16 characters remains a practical baseline for sensitive bank, email, and work logins, while 32 or more is better for master passwords and server credentials.",
      },
      {
        kind: "table",
        tables: [
          {
            title: "Password Length vs. Brute Force Protection (2026)",
            description:
              "Estimated time for modern hardware to crack passwords based on length and complexity.",
            columns: [
              "Length",
              "Character Types",
              "Security Level",
              "Crack Time (Estimated)",
            ],
            rows: [
              {
                Length: "8",
                "Character Types": "Alphanumeric",
                "Security Level": "Low",
                "Crack Time (Estimated)": "Minutes",
              },
              {
                Length: "12",
                "Character Types": "Mix + Symbols",
                "Security Level": "Medium",
                "Crack Time (Estimated)": "Years",
              },
              {
                Length: "16",
                "Character Types": "Mix + Symbols",
                "Security Level": "High",
                "Crack Time (Estimated)": "Centuries",
              },
              {
                Length: "32+",
                "Character Types": "All Types",
                "Security Level": "Paranoid",
                "Crack Time (Estimated)": "Infinite",
              },
            ],
            note: "Use this as practical guidance rather than a guarantee. Reused or leaked passwords remain risky even when they are long.",
            emphasisColumns: ["Security Level"],
          },
        ],
      },
    ],
    faqs: [
      {
        question: "Is it safe to use an online password generator?",
        answer:
          "Yes, provided the tool does not log or store your results. Snappy-Fix is designed for a configure, generate, review, and copy workflow. Your generated password is returned to the page as a result, and you should move it directly into a trusted password manager.",
      },
      {
        question: "What are ambiguous characters in passwords?",
        answer:
          "Ambiguous characters are symbols that look similar and are easy to misread, such as 'i', 'I', 'l', and '1', or '0' and 'O'. Our tool allows you to exclude these to ensure your password is easy to read and type manually.",
      },
      {
        question: "What does force include characters mean?",
        answer:
          "Force include characters means the generator tries to place your required letters, numbers, or symbols into the final password. This is helpful when a website requires a specific symbol, or when you want a password generator using your letters without typing the entire password yourself.",
      },
      {
        question: "Why is 16 characters now the recommended minimum?",
        answer:
          "Short passwords are easier to crack, especially when they follow human patterns. A 16 character password generator gives you more entropy than an 8 or 12 character password, especially when uppercase, lowercase, numbers, and symbols are enabled.",
      },
      {
        question: "Does this tool work as a wifi password key generator?",
        answer:
          "Absolutely. You can set the length to 64 characters (the max for WPA2) and choose the alphanumeric option to create a secure key for your home network.",
      },
      {
        question: "Can I use this for a crypto wallet or master password?",
        answer:
          "Yes. For sensitive master passwords, admin logins, or wallet-related accounts, use at least 32 characters with all available character types. Store the result in a password manager and never reuse it elsewhere.",
      },
      {
        question: "Do you offer a 128 character password generator?",
        answer:
          "Our tool actually goes beyond that, supporting up to 512 characters. While 128 is excellent for encryption keys, 512 provides maximum security for server-side configurations.",
      },
    ],
    benefits: [
      {
        icon: "Zap",
        title: "Strong Random Passwords",
        description:
          "Create high-entropy passwords for banking, email, wifi, PDF locks, server accounts, and other sensitive logins.",
      },
      {
        icon: "EyeOff",
        title: "Exclusions for Readability",
        description:
          "Remove confusing characters such as 0, O, 1, l, or any symbols a platform rejects, while keeping the password random.",
      },
      {
        icon: "Settings",
        title: "Forced Included Characters",
        description:
          "Require specific letters, numbers, or special characters in the output when an account form has strict password rules.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Set password length",
        description:
          "Enter your required password length. The valid range is 4 to 512 characters. We recommend at least 16 characters for most modern accounts.",
      },
      {
        number: "02",
        title: "Choose character types",
        description:
          "Toggle the character type checkboxes: Uppercase Letters, Lowercase Letters, Numbers, and Symbols. Enable Symbols for maximum strength.",
      },
      {
        number: "03",
        title: "Exclude or include characters",
        description:
          "Optionally remove characters you want to avoid in the Exclude field, or force specific characters to appear using the Force Include field.",
      },
      {
        number: "04",
        title: "Generate and copy",
        description:
          "Click Generate Password, review the strength meter, and copy the result into your password manager or account setup flow.",
      },
    ],
    useCases: [
      {
        label: "Account Registration",
        icon: "UserPlus",
        description:
          "Create secure unique passwords for social media, banking, shopping, work, and subscription accounts.",
        accent: "primary",
      },
      {
        label: "WiFi Security",
        icon: "Wifi",
        description:
          "Generate a tough WPA2 or WPA3 key with the right length and character set for your router.",
        accent: "primary",
      },
      {
        label: "Encryption Keys",
        icon: "Key",
        description:
          "Generate long random strings for PDF passwords, admin credentials, backup keys, and encryption workflows.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Security-Conscious Users",
        icon: "Shield",
        description:
          "People who want the best password generator free workflow for avoiding reused or predictable credentials.",
        accent: "accent",
      },
      {
        label: "IT Professionals",
        icon: "Terminal",
        description:
          "System admins who need a hard password generator for server passwords, API keys, database credentials, and team onboarding.",
        accent: "accent",
      },
      {
        label: "Remote Workers",
        icon: "Laptop",
        description:
          "Employees who need strong email passwords, VPN logins, and work-from-home credentials that meet company rules.",
        accent: "accent",
      },
    ],
  },
};

export default securePasswordGeneratorTool;
export { securePasswordGeneratorTool };
